import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import config from '../config';
import '../styles/CompleteProfileModal.css';

const CompleteProfileModal = ({ isOpen, onClose, onComplete, requiredFields = ['date_of_birth', 'sex'] }) => {
    const [formData, setFormData] = useState({
        date_of_birth: '',
        sex: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedDay, setSelectedDay] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedYear, setSelectedYear] = useState('');

    const calculateAge = (birthDate) => {
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        
        return age;
    };

    const getMaxYear = () => {
        return new Date().getFullYear() - 15;
    };

    const getMinYear = () => {
        return new Date().getFullYear() - 120;
    };

    const getDaysInMonth = (year, month) => {
        if (!year || !month) return 31;
        return new Date(year, month, 0).getDate();
    };

    const months = [
        { value: '01', label: 'Janvier' },
        { value: '02', label: 'Février' },
        { value: '03', label: 'Mars' },
        { value: '04', label: 'Avril' },
        { value: '05', label: 'Mai' },
        { value: '06', label: 'Juin' },
        { value: '07', label: 'Juillet' },
        { value: '08', label: 'Août' },
        { value: '09', label: 'Septembre' },
        { value: '10', label: 'Octobre' },
        { value: '11', label: 'Novembre' },
        { value: '12', label: 'Décembre' }
    ];

    const years = [];
    for (let year = getMaxYear(); year >= getMinYear(); year--) {
        years.push(year);
    }

    const days = [];
    const maxDays = getDaysInMonth(selectedYear, selectedMonth);
    for (let day = 1; day <= maxDays; day++) {
        days.push(day);
    }

    // Update date_of_birth when day, month, or year changes
    useEffect(() => {
        if (selectedDay && selectedMonth && selectedYear) {
            const dateString = `${selectedYear}-${selectedMonth}-${selectedDay.toString().padStart(2, '0')}`;
            handleChange('date_of_birth', dateString);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDay, selectedMonth, selectedYear]);

    // Parse existing date_of_birth when modal opens
    useEffect(() => {
        if (formData.date_of_birth) {
            const [year, month, day] = formData.date_of_birth.split('-');
            setSelectedYear(year);
            setSelectedMonth(month);
            setSelectedDay(parseInt(day));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validateForm = () => {
        const newErrors = {};

        if (requiredFields.includes('date_of_birth')) {
            if (!formData.date_of_birth) {
                newErrors.date_of_birth = 'La date de naissance est requise';
            } else {
                const age = calculateAge(formData.date_of_birth);
                if (age < 15) {
                    newErrors.date_of_birth = 'Vous devez avoir au moins 15 ans pour utiliser cette plateforme';
                } else if (age > 120) {
                    newErrors.date_of_birth = 'Veuillez entrer une date de naissance valide';
                }
            }
        }

        if (requiredFields.includes('sex') && !formData.sex) {
            newErrors.sex = 'Le sexe est requis';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const isFormComplete = () => {
        if (requiredFields.includes('date_of_birth') && !formData.date_of_birth) {
            return false;
        }
        if (requiredFields.includes('sex') && !formData.sex) {
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const token = Cookies.get('authToken');
            if (!token) {
                throw new Error('Non authentifié');
            }

            const age = calculateAge(formData.date_of_birth);

            const response = await fetch(`${config.backendUrl}/users/me/update/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`,
                },
                body: JSON.stringify({
                    date_of_birth: formData.date_of_birth,
                    age: age,
                    sex: formData.sex
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Erreur lors de la mise à jour du profil');
            }

            const updatedUser = await response.json();

            // Update localStorage with new user data
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            const updatedStoredUser = { 
                ...storedUser, 
                date_of_birth: updatedUser.date_of_birth,
                age: updatedUser.age, 
                sex: updatedUser.sex 
            };
            localStorage.setItem('user', JSON.stringify(updatedStoredUser));

            // Call the onComplete callback with updated user data
            if (onComplete) {
                onComplete(updatedUser);
            }

            onClose();
        } catch (error) {
            console.error('Error updating profile:', error);
            setErrors({
                submit: error.message || 'Impossible de mettre à jour le profil. Veuillez réessayer.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    // Early return AFTER all hooks
    if (!isOpen) return null;

    return (
        <div className="complete-profile-modal-overlay">
            <div className="complete-profile-modal">
                <div className="complete-profile-modal-header">
                    <h2>Complétez votre profil</h2>
                    <p>Pour continuer, veuillez compléter les informations suivantes :</p>
                </div>

                <form onSubmit={handleSubmit} className="complete-profile-form">
                    {requiredFields.includes('date_of_birth') && (
                        <div className="form-group">
                            <label>Date de naissance *</label>
                            <div className="date-selects-wrapper">
                                <select
                                    value={selectedDay}
                                    onChange={(e) => setSelectedDay(e.target.value)}
                                    className={`date-select ${errors.date_of_birth ? 'error' : ''}`}
                                    disabled={isSubmitting}
                                >
                                    <option value="">Jour</option>
                                    {days.map(day => (
                                        <option key={day} value={day}>{day}</option>
                                    ))}
                                </select>
                                <select
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                    className={`date-select ${errors.date_of_birth ? 'error' : ''}`}
                                    disabled={isSubmitting}
                                >
                                    <option value="">Mois</option>
                                    {months.map(month => (
                                        <option key={month.value} value={month.value}>{month.label}</option>
                                    ))}
                                </select>
                                <select
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(e.target.value)}
                                    className={`date-select ${errors.date_of_birth ? 'error' : ''}`}
                                    disabled={isSubmitting}
                                >
                                    <option value="">Année</option>
                                    {years.map(year => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>
                            
                            {errors.date_of_birth && <span className="error-text">{errors.date_of_birth}</span>}
                            {formData.date_of_birth && !errors.date_of_birth && (
                                <span className="info-text">Âge: {calculateAge(formData.date_of_birth)} ans</span>
                            )}
                        </div>
                    )}

                    {requiredFields.includes('sex') && (
                        <div className="form-group">
                            <label htmlFor="sex">Sexe *</label>
                            <select
                                id="sex"
                                value={formData.sex}
                                onChange={(e) => handleChange('sex', e.target.value)}
                                className={errors.sex ? 'error' : ''}
                                disabled={isSubmitting}
                            >
                                <option value="">Sélectionnez votre sexe</option>
                                <option value="M">Homme</option>
                                <option value="F">Femme</option>
                            </select>
                            {errors.sex && <span className="error-text">{errors.sex}</span>}
                        </div>
                    )}

                    {errors.submit && (
                        <div className="submit-error">
                            {errors.submit}
                        </div>
                    )}

                    <div className="modal-actions">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn-cancel"
                            disabled={isSubmitting}
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            className="btn-submit"
                            disabled={isSubmitting || !isFormComplete()}
                        >
                            {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CompleteProfileModal;
